import React, { useState, useEffect } from 'react';
import { Package, Filter } from 'lucide-react';
import { productsAPI, categoriesAPI } from '../api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ProductGallery } from './ProductGallery';

export const FeaturedProducts = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const [categoriesRes] = await Promise.all([
        categoriesAPI.getAll()
      ]);
      setCategories(categoriesRes.data);
      await fetchProducts();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const categoryId = selectedCategory === 'all' ? null : parseInt(selectedCategory);
      const response = await productsAPI.getAll(categoryId);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products;

  return (
    <section id="products" className="py-20 px-[5%]" style={{ background: 'var(--light)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-12">
          <span
            className="inline-block font-extrabold text-xs tracking-[2px] uppercase mb-[10px]"
            style={{ color: 'var(--pink-d)' }}
          >
            Featured
          </span>
          <h2
            className="fredoka mb-2"
            style={{
              fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
              color: 'var(--dark)',
            }}
          >
            Kids Are Loving These 💕
          </h2>
          <p
            className="text-base leading-relaxed max-w-[520px]"
            style={{ color: 'var(--mid)' }}
          >
            Our most-loved picks — restocking fast!
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
          <div className="flex items-center space-x-2 text-[#736c64]">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">Filter:</span>
          </div>
          <Button
            onClick={() => setSelectedCategory('all')}
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            className={
              selectedCategory === 'all'
                ? 'bg-[#61525a] hover:bg-[#4a3f45] text-white'
                : 'border-[#61525a]/30 text-[#61525a] hover:bg-[#61525a]/10'
            }
          >
            All Products
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id.toString())}
              variant={selectedCategory === cat.id.toString() ? 'default' : 'outline'}
              className={
                selectedCategory === cat.id.toString()
                  ? 'bg-[#61525a] hover:bg-[#4a3f45] text-white'
                  : 'border-[#61525a]/30 text-[#61525a] hover:bg-[#61525a]/10'
              }
            >
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-lg text-[#736c64]">Loading products...</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product, index) => (
            <Card
              key={product.id}
              className="group overflow-hidden border-2 border-transparent hover:border-[#61525a] transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2 cursor-pointer animate-in fade-in slide-in-from-bottom-4"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <CardContent className="p-0">
                {/* Product Image Gallery */}
                <div className="relative bg-white">
                  <ProductGallery 
                    images={product.images || (product.image ? [product.image] : [])} 
                    productName={product.name}
                  />
                  {product.featured && (
                    <Badge className="absolute top-3 right-3 bg-[#ff8c19] text-white border-0 z-10">
                      Featured
                    </Badge>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-5 space-y-3 bg-white">
                  <div>
                    <Badge
                      variant="outline"
                      className="mb-2 text-xs border-[#61525a]/30 text-[#736c64]"
                    >
                      {product.category}
                    </Badge>
                    <h3 className="text-lg font-bold text-[#1e1919] group-hover:text-[#61525a] transition-colors duration-200">
                      {product.name}
                    </h3>
                  </div>

                  <p className="text-sm text-[#736c64] line-clamp-2">
                    {product.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-[#736c64] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#1e1919] mb-2">
              No products found
            </h3>
            <p className="text-[#736c64]">
              Try selecting a different category
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
